import { useEffect, useCallback, forwardRef, useState, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Skeleton,
  Box,
  Fade,
  Tooltip,
} from "@mui/material";
import {
  CloseRounded as CloseRoundedIcon,
  SendRounded as SendRoundedIcon,
} from "@mui/icons-material";
import { styled, alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import axios from "axios";
import CommentService from "src/services/Comment.service";
import { format } from "timeago.js";
import { logEvent } from "firebase/analytics";
import { analytics } from "src/config/firebase.config";
import GradientText from "src/components/GradientText";

// Styled components with glassmorphic design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 9999,
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(12px)",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 9998,
  },
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(3),
    background: `linear-gradient(145deg, 
      ${alpha(theme.palette.background.paper, 0.95)} 0%, 
      ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
    backdropFilter: "blur(20px)",
    border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
    boxShadow: `
      0 8px 32px ${alpha(theme.palette.common.black, 0.15)},
      0 2px 16px ${alpha(theme.palette.common.black, 0.1)},
      inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
    `,
    maxHeight: "85vh",
    zIndex: 9999,
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, 
        transparent 0%, 
        ${alpha(theme.palette.primary.main, 0.3)} 50%, 
        transparent 100%)`,
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(3),
    background: `linear-gradient(145deg, 
      ${alpha(theme.palette.background.paper, 0.8)} 0%, 
      ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    transition: "all 0.3s ease",
    "&:hover": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    "&.Mui-focused": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}));

const CommentItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.default, 0.3)} 0%, 
    ${alpha(theme.palette.background.default, 0.1)} 100%)`,
  backdropFilter: "blur(5px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(145deg, 
      ${alpha(theme.palette.background.default, 0.4)} 0%, 
      ${alpha(theme.palette.background.default, 0.2)} 100%)`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(6, 3),
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.default, 0.2)} 0%, 
    ${alpha(theme.palette.background.default, 0.05)} 100%)`,
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function DiscussionModal({ open, setOpen, articleid, owner }) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasPressedEnter, setHasPressedEnter] = useState(false);
  const [comments, setComments] = useState([]);
  const [discussion, setDiscussion] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const commentInputRef = useRef(null);

  const handleClose = useCallback(() => {
    setOpen(false);
    setDiscussion("");
    setComments([]);
  }, [setOpen]);

  const handleCommentSubmit = useCallback(async () => {
    if (discussion.trim() === "" || isSubmitting) {
      return;
    }

    const commentData = {
      comment: discussion.trim(),
      article: articleid,
      owner: owner,
    };

    try {
      setIsSubmitting(true);
      await CommentService.createComment(commentData);
      setDiscussion("");
      setHasPressedEnter(false);
      await fetchComments();
    } catch (error) {
      console.error("Error creating comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [discussion, articleid, owner, isSubmitting]);

  const fetchComments = useCallback(async () => {
    if (!articleid) return;

    try {
      setIsLoading(true);
      const fetchedComments = await CommentService.fetchComments(articleid);
      if (fetchedComments) {
        setComments(fetchedComments);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [articleid]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleCommentSubmit();
      } else if (e.key === "Escape") {
        handleClose();
      }
    },
    [handleCommentSubmit, handleClose]
  );

  // Effects
  useEffect(() => {
    if (!open) return;

    logEvent(analytics, "button_click", { button_name: "View comments" });
    fetchComments();
  }, [open, fetchComments]);

  useEffect(() => {
    if (hasPressedEnter) {
      handleCommentSubmit();
    }
  }, [hasPressedEnter, handleCommentSubmit]);

  // Loading skeleton component
  const CommentSkeleton = ({ index }) => (
    <CommentItem key={index}>
      <ListItemAvatar>
        <Skeleton animation="wave" variant="circular" width={40} height={40} />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Skeleton animation="wave" height={20} width={80} />
            <Typography sx={{ opacity: 0.5 }}>•</Typography>
            <Skeleton animation="wave" height={16} width={60} />
          </Stack>
        }
        secondary={
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Skeleton animation="wave" height={16} width="100%" />
            <Skeleton animation="wave" height={16} width="70%" />
          </Stack>
        }
      />
    </CommentItem>
  );

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              Discussion
            </Typography>
          </Box>
          <Tooltip title="Close (Esc)">
            <IconButton
              onClick={handleClose}
              size="small"
              color="error"
              sx={{
                bgcolor: alpha("#fff", 0.1),
                backdropFilter: "blur(10px)",
                "&:hover": {
                  bgcolor: alpha("#fff", 0.2),
                },
                transition: "all 0.3s ease",
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ minHeight: "60vh", pb: 1 }}>
        {isLoading ? (
          <List>
            {Array.from({ length: 3 }, (_, index) => (
              <CommentSkeleton key={index} index={index} />
            ))}
          </List>
        ) : comments.length === 0 ? (
          <EmptyStateContainer>
            <Box
              component="img"
              sx={{
                width: 200,
                height: 200,
                mx: "auto",
                display: "block",
                opacity: 0.6,
                filter: "grayscale(20%)",
              }}
              src="/assets/illustrations/comments.png"
              alt="No comments"
            />
            <GradientText
              variant="h5"
              sx={{
                mt: 3,
                mb: 1,
              }}
            >
              Start the Discussion
            </GradientText>
            <Typography variant="body2" color="text.secondary">
              Be the first to share your thoughts on this article
            </Typography>
          </EmptyStateContainer>
        ) : (
          <List>
            {comments.map((comment, index) => (
              <Fade in timeout={200 + index * 100} key={comment._id}>
                <CommentItem>
                  <ListItemAvatar>
                    <Avatar
                      src={
                        comment.owner?.profilePic ||
                        "https://avatar.iran.liara.run/public/boy"
                      }
                      alt={comment.owner?.name || "Anonymous"}
                      sx={{
                        width: 40,
                        height: 40,
                        border: `2px solid ${alpha(
                          theme.palette.primary.main,
                          0.2
                        )}`,
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {comment.owner?.name || "Anonymous"}
                        </Typography>
                        <Typography sx={{ opacity: 0.5, fontSize: "0.875rem" }}>
                          •
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(comment.createdAt, "en_US")}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          lineHeight: 1.6,
                          color: "text.primary",
                        }}
                      >
                        {comment.comment}
                      </Typography>
                    }
                  />
                </CommentItem>
              </Fade>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <StyledTextField
          fullWidth
          ref={commentInputRef}
          placeholder="Share your thoughts..."
          value={discussion}
          onChange={(e) => setDiscussion(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={3}
          disabled={isSubmitting}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Send (Enter)">
                  <IconButton
                    onClick={handleCommentSubmit}
                    disabled={!discussion.trim() || isSubmitting}
                    sx={{
                      bgcolor: discussion.trim()
                        ? alpha(theme.palette.primary.main, 0.15)
                        : alpha("#000", 0.05),
                      color: discussion.trim()
                        ? theme.palette.primary.main
                        : "text.disabled",
                      "&:hover": {
                        bgcolor: discussion.trim()
                          ? alpha(theme.palette.primary.main, 0.25)
                          : alpha("#000", 0.1),
                        transform: discussion.trim() ? "scale(1.05)" : "none",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <SendRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </DialogActions>
    </StyledDialog>
  );
}
